import { useGetUserById } from '@/api/get-user-by-id.hook'

import { cn } from '@/lib/ui/style-helpers'

import { AuthorLine } from '@/components/lemmala-design-system/author-line/author-line'
import { UserAvatar } from '@/components/shared/user-avatar/user-avatar'

export type TimelineItemProps = {
  userId?: string
  date?: string
  children: React.ReactNode
  compactAvatar?: boolean
  userName?: string
  // Used with userName, for authors the server already resolved.
  avatarUrl?: string
  icon?: React.ReactNode
  overrideUsername?: string
  fullWidth?: boolean
}

export const TimelineItem = ({
  userId,
  userName,
  avatarUrl,
  date,
  children,
  compactAvatar,
  icon,
  overrideUsername,
  fullWidth,
}: TimelineItemProps) => {
  const authorDate = compactAvatar ? undefined : date

  return (
    <div className='relative flex flex-col'>
      <div className={cn('flex items-start', compactAvatar ? 'flex-row' : 'flex-col gap-1')}>
        {userName ? (
          <AuthorLine
            name={userName}
            avatarUrl={avatarUrl}
            date={authorDate}
            hideName={compactAvatar}
          />
        ) : (
          <ResolvedAuthorLine
            userId={userId ?? 'Lema'}
            icon={icon}
            date={authorDate}
            hideName={compactAvatar}
            overrideUsername={overrideUsername}
          />
        )}
        <div
          className={cn('flex-1', !compactAvatar ? 'ml-8' : 'ml-1.5', fullWidth && 'self-stretch')}
        >
          {children}
        </div>
      </div>
    </div>
  )
}

type ResolvedAuthorLineProps = {
  userId: string
  icon?: React.ReactNode
  date?: string
  hideName?: boolean
  overrideUsername?: string
}

const ResolvedAuthorLine = ({
  userId,
  icon,
  date,
  hideName,
  overrideUsername,
}: ResolvedAuthorLineProps) => {
  const { user, isError } = useGetUserById(userId)

  // A 404 author has no UserAvatar to draw, so leave the slot empty and let AuthorLine show its name chip.
  const fallbackAvatar = isError ? undefined : (
    <UserAvatar userId={userId} fallbackName={overrideUsername} withTooltip={false} />
  )

  return (
    <AuthorLine
      name={overrideUsername ?? user?.name ?? ''}
      avatar={icon ?? fallbackAvatar}
      date={date}
      hideName={hideName}
    />
  )
}
